import { WebStorageStateStore, type User } from "oidc-client-ts";

export type SigninState = {
  returnSearch?: string;
};

/**
 * Carries through the original URL search string so it can be restored
 * after a redirect-based OIDC login.
 */
export function buildSigninStateFromSearch(
  search: string,
): SigninState | undefined {
  const searchParams = new URLSearchParams(search);
  if ([...searchParams.keys()].length < 1) {
    return undefined;
  }

  return { returnSearch: search };
}

function normalizeBasePath(basePath: string): string {
  if (!basePath || basePath === "." || basePath === "/") return "/";
  return `/${basePath.replace(/^\/|\/$/g, "")}/`;
}

interface OidcConfigOptions {
  oidcAuthority: string;
  oidcClientId: string;
  oidcScope: string;
  basePath?: string; // Optional
  onSigninCallback?: (user: User | undefined) => void;
}

// OpenID (OID) configuration factory
export function createOidcConfig(options: OidcConfigOptions) {
  const normalizedBasePath = normalizeBasePath(options.basePath ?? "/");
  const redirectUri = new URL(
    normalizedBasePath,
    window.location.origin,
  ).toString();

  return {
    authority: options.oidcAuthority,
    client_id: options.oidcClientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: redirectUri,
    response_type: "code",
    scope: options.oidcScope,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: true,
    onSigninCallback: (user: User | undefined) =>
      options.onSigninCallback?.(user),
  };
}
